<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTicketsStore } from '@/stores/tickets'
import AppIcon from '@/components/AppIcon.vue'
import AppTooltip from '@/components/AppTooltip.vue'
import NotificationBell from '@/components/NotificationBell.vue'
import { newsOpen } from '@/lib/novidades'

const auth = useAuthStore()
const route = useRoute()
const router = useRouter()
const cortexIconUrl = '/brand/gestao-marca-branca.svg' // faixa escura: marca branca, sem fundo
const profileOpen = ref(false)

// Chamados com novidade pra pessoa (como a bolinha do cortexwoot, mas por pessoa).
const tickets = useTicketsStore()
onMounted(() => tickets.fetchNews())
const badge = n => (n > 99 ? '99+' : n)
const spaceNews = space =>
  space.groups.flatMap(group => group.items).reduce((sum, item) => sum + (item.news ? tickets.news[item.news] || 0 : 0), 0)

const initial = computed(() => (auth.user?.name || '?').slice(0, 1).toUpperCase())

const logout = async () => {
  profileOpen.value = false
  await auth.logout()
  router.push({ name: 'login' })
}

const spaces = [
  {
    id: 'support',
    label: 'Suporte',
    description: 'Central de atendimento',
    icon: 'support',
    accent: '#98c759',
    groups: [
      {
        label: 'Atendimento',
        items: [
          { name: 'chamado', routes: ['inbox', 'chamado-quadro'], label: 'Chamados', icon: 'inbox', roles: ['admin', 'agent'], news: 'atendimento' },
          { name: 'queue', label: 'Minha fila', icon: 'target', roles: ['external'], news: 'atendimento' },
        ],
      },
      {
        label: 'Acompanhar',
        items: [
          { name: 'management', label: 'Visão geral', icon: 'chart', roles: ['admin'] },
          { name: 'reports', label: 'Relatórios', icon: 'chart', roles: ['admin'] },
          { name: 'channel-health', label: 'Saúde dos canais', icon: 'bell', roles: ['admin'] },
          // "IA nas contas" oculta temporariamente (rota vira redirect no router).
        ],
      },
      {
        label: 'Configuração',
        items: [
          { name: 'accounts', label: 'Contas', icon: 'building', roles: ['admin'] },
          { name: 'agents', label: 'Agentes', icon: 'users', roles: ['admin'] },
          { name: 'teams', label: 'Times', icon: 'team', roles: ['admin'] },
          { name: 'sla', label: 'Status e SLA interno', icon: 'clock', roles: ['admin'] },
        ],
      },
    ],
  },
  {
    id: 'implantation',
    label: 'Implantação',
    description: 'Operação e estrutura',
    icon: 'implantation',
    accent: '#14a88d',
    groups: [
      {
        label: 'Workspace',
        items: [
          { name: 'implantation', label: 'Em construção', icon: 'implantation', roles: ['admin'] },
        ],
      },
    ],
  },
]

const allowedSpaces = computed(() =>
  spaces
    .map(space => ({
      ...space,
      groups: space.groups
        .map(group => ({ ...group, items: group.items.filter(item => item.roles.includes(auth.profile)) }))
        .filter(group => group.items.length),
    }))
    .filter(space => space.groups.length)
)

const spaceForRoute = name =>
  allowedSpaces.value.find(space =>
    space.groups.some(group => group.items.some(item => item.name === name || item.routes?.includes(name)))
  )

const activeSpaceId = ref(spaceForRoute(route.name)?.id || allowedSpaces.value[0]?.id)
const panelOpen = ref(true)
const activeSpace = computed(() => allowedSpaces.value.find(space => space.id === activeSpaceId.value))

const selectSpace = id => {
  if (activeSpaceId.value === id) {
    panelOpen.value = !panelOpen.value
    return
  }
  activeSpaceId.value = id
  panelOpen.value = true
}

watch(
  () => route.name,
  name => {
    const match = spaceForRoute(name)
    if (match) activeSpaceId.value = match.id
  }
)
</script>

<template>
  <aside class="sidebar-shell flex shrink-0">
    <div class="icon-rail flex w-[68px] flex-col items-center border-r border-white/10 bg-[#152745] py-3 text-white">
      <div class="mb-5 grid size-10 place-items-center">
        <img :src="cortexIconUrl" alt="Cortex Gestão" class="h-7 w-auto" />
      </div>

      <nav class="flex flex-1 flex-col items-center gap-2">
        <AppTooltip
          v-for="space in allowedSpaces"
          :key="space.id"
          :text="space.label"
          placement="right"
        >
          <button
            type="button"
            class="group relative grid size-11 place-items-center rounded-xl text-white/55 transition-all hover:bg-white/10 hover:text-white"
            :class="{ 'bg-white/12 text-white shadow-inner': activeSpaceId === space.id && panelOpen }"
            :aria-label="space.label"
            @click="selectSpace(space.id)"
          >
            <span
              v-if="activeSpaceId === space.id && panelOpen"
              class="absolute -left-3 h-6 w-[3px] rounded-r-full"
              :style="{ backgroundColor: space.accent }"
            />
            <AppIcon :name="space.icon" :size="20" />
            <span
              v-if="spaceNews(space)"
              class="absolute -right-1 top-0 grid min-w-4 place-items-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold leading-4 text-white"
              :aria-label="`${spaceNews(space)} chamados com novidade`"
            >
              {{ badge(spaceNews(space)) }}
            </span>
          </button>
        </AppTooltip>
      </nav>

      <div class="mb-1 flex flex-col items-center gap-1 border-t border-white/10 pt-3">
        <NotificationBell sidebar />

        <div class="relative">
          <AppTooltip :text="auth.user?.name || 'Perfil'" placement="right">
            <button
              type="button"
              :aria-label="auth.user?.name || 'Perfil'"
              aria-haspopup="menu"
              :aria-expanded="profileOpen"
              class="grid size-10 place-items-center rounded-xl bg-white/10 text-xs font-bold text-white transition-colors hover:bg-white/20"
              @click="profileOpen = !profileOpen"
            >
              {{ initial }}
            </button>
          </AppTooltip>

          <div v-if="profileOpen" class="fixed inset-0 z-10" @click="profileOpen = false" />

          <div
            v-if="profileOpen"
            role="menu"
            class="absolute bottom-0 left-full z-20 ml-3 w-60 overflow-hidden rounded-xl border border-[#e1e8dc] bg-white text-left shadow-lg"
          >
            <div class="border-b border-[#eef3ea] px-4 py-3">
              <p class="truncate text-[13px] font-bold text-[#1f3350]">{{ auth.user?.name || 'Visitante' }}</p>
              <p class="truncate text-[11px] text-[#8b998f]">{{ auth.user?.email }}</p>
            </div>

            <button
              v-if="!auth.user?.external"
              type="button"
              role="menuitem"
              class="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-[13px] font-medium text-[#334866] transition hover:bg-[#f3f8ed]"
              @click="profileOpen = false; newsOpen = true"
            >
              <AppIcon name="sparkle" :size="16" class="text-[#6f9b36]" />
              Novidades
            </button>

            <a
              v-if="auth.isAdmin"
              href="/sidekiq"
              target="_blank"
              rel="noopener"
              role="menuitem"
              class="flex items-center gap-2.5 px-4 py-2.5 text-[13px] font-medium text-[#334866] transition hover:bg-[#f3f8ed]"
              @click="profileOpen = false"
            >
              <AppIcon name="chart" :size="16" class="text-[#6f9b36]" />
              Painel Sidekiq
            </a>

            <button
              type="button"
              role="menuitem"
              class="flex w-full items-center gap-2.5 border-t border-[#eef3ea] px-4 py-2.5 text-left text-[13px] font-medium text-[#b94d44] transition hover:bg-[#fff0ee]"
              @click="logout"
            >
              <AppIcon name="lock" :size="16" />
              Sair
            </button>
          </div>
        </div>
      </div>

      <AppTooltip text="Recolher menu" placement="right">
        <button
          type="button"
          aria-label="Recolher menu"
          class="grid size-10 place-items-center rounded-xl text-white/45 transition-colors hover:bg-white/10 hover:text-white"
          @click="panelOpen = !panelOpen"
        >
          <AppIcon name="collapse" :size="17" />
        </button>
      </AppTooltip>
    </div>

    <transition name="sidebar-panel">
      <div v-if="panelOpen && activeSpace" class="sidebar-panel flex w-[238px] flex-col border-r border-[#dce4d7] bg-[#f7f9f4]">
        <header class="border-b border-[#e2e9dd] px-4 pb-4 pt-[18px]">
          <div class="flex items-center gap-3">
            <span class="grid size-9 place-items-center rounded-xl text-white shadow-sm" :style="{ backgroundColor: activeSpace.accent }">
              <AppIcon :name="activeSpace.icon" :size="18" />
            </span>
            <div class="min-w-0">
              <h2 class="truncate text-[14px] font-bold text-[#152745]">{{ activeSpace.label }}</h2>
              <p class="truncate text-[11px] text-[#7d8b82]">{{ activeSpace.description }}</p>
            </div>
          </div>
        </header>

        <nav class="flex-1 overflow-y-auto px-2.5 py-4">
          <section v-for="group in activeSpace.groups" :key="group.label" class="mb-6">
            <p class="mb-1.5 px-2 text-[10px] font-bold uppercase tracking-[0.11em] text-[#98a59b]">{{ group.label }}</p>
            <router-link
              v-for="item in group.items"
              :key="item.name"
              :to="{ name: item.name }"
              class="group mb-0.5 flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-[#637169] transition-colors hover:bg-white hover:text-[#152745]"
              active-class="bg-white text-[#152745] shadow-sm ring-1 ring-[#dfe8d9]"
            >
              <AppIcon :name="item.icon" :size="16" class="text-[#89968e] group-hover:text-[#6f9b36]" />
              <span class="truncate">{{ item.label }}</span>
              <span
                v-if="item.news && tickets.news[item.news]"
                class="ml-auto rounded-full bg-rose-600 px-1.5 text-[10px] font-semibold leading-4 text-white"
                :aria-label="`${tickets.news[item.news]} com novidade`"
              >
                {{ badge(tickets.news[item.news]) }}
              </span>
            </router-link>
          </section>
        </nav>
      </div>
    </transition>
  </aside>
</template>
